<style scoped lang="scss"></style>

<template>
  <div>
    <div class="uk-form" v-if="application">
      <fieldset data-uk-margin>
        <p>We collect personal information about the people we serve in a computer system called Minnesota’s HMIS (Homeless Management Information System). Many social service agencies use this computer system, including street outreach, shelters, and housing programs. The HMIS Governing Board may amend this Data Privacy Notice and the Release of Information, at any time without notice or consent. The Data Privacy Notice and Release of Information currently available can be found on the MN HMIS website at <a href="http://hmismn.org">hmismn.org</a>.</p>

        <h2>What kinds of information are collected in HMIS?</h2>
        <ul>
          <li>
            <strong>Basic Information:</strong> This is information that rarely or never changes for an individual. This includes, but may not be limited to:
            <ul>
              <li>Full Name</li>
              <li>Date of Birth</li>
              <li>Social Security Number</li>
              <li>Demographics</li>
              <li>Basic contact information</li>
            </ul>
            Collection of this information ensures duplicate client records are not created in HMIS. This information is visible by default to all HMIS participating agencies.
          </li>
          <li>
            <strong>Transactional Information:</strong> This is information about services a client receives, specific programs they are enrolled in, and more in-depth personal history about their experience of homelessness and barriers. This information is only visible to all HMIS participating agencies if the client grants consent.
          </li>
        </ul>

        <h2>Why do we collect this information?</h2>
        <ul>
          <li>To help keep this program and others like it going. We are required to use HMIS.</li>
          <li>So we know how many people we serve and the types of people we serve at our agency and in the state.</li>
          <li>So we all understand what people need and can plan services to meet these needs.</li>
        </ul>

        <h2>Who can see information that is in Minnesota’s HMIS?</h2>
        <ul>
          <li>People who work for this agency will use it to help provide services to you or your family.</li>
          <li>
            Other agencies like this agency that provide services and have received permission from you to see your information. The agencies that participate in Minnesota’s HMIS may change from time to time. A copy of the current list of participating agencies is available upon request.
          </li>
          <li>
            Auditors or funders who have legal rights to review the work of this agency, such as the U.S. Department of Housing and Urban Development and other state or local government entities.
          </li>
          <li>
            Organizations that run, administer, and work on the system, such as the Institute for Community Alliances or Local System Administrators. When these organizations work on the system, they may see information about you.
          </li>
          <li>
            People using HMIS information to do research and write reports, including, but not limited to, the Minnesota Department of Human Services (DHS). Your personally identifiable information will never appear in research reports.
          </li>
          <li>
            The law says we have to report physical or sexual abuse of children and vulnerable adults. If we think there is abuse or neglect in your household, we will report it to Child or Adult Protection.
          </li>
          <li>We may release your information to protect the health or safety of you or others as required by law.</li>
          <li>Others as required by law, including officials with a valid subpoena, warrant, or court order.</li>
        </ul>

        <h2>How is your privacy protected?</h2>
        <ul>
          <li>All users of data must sign an agreement to protect your privacy and comply with state and federal laws and policies before seeing any information.</li>
          <li>The computer program used for this purpose has industry-standard security protocols and is updated regularly to meet these security requirements.</li>
          <li>We will not release your information for any other use unless you permit us in writing.</li>
        </ul>

        <h2>What are your rights?</h2>
        <ul>
          <li>If you do not want your name, social security number, or date of birth entered in HMIS, tell the intake worker. This agency will not refuse to help you for denying this. However, federal and state regulations may require limited data collection for funding purposes.</li>
          <li>You have the right to request a copy of the Minnesota’s HMIS information about you.</li>
          <li>You have the right to correct mistakes in HMIS information about you.</li>
          <li>
            If you think this agency or Minnesota’s HMIS violated your privacy rights, you have the right to complain or appeal. Ask a staff person for a complaint and appeal form.
          </li>
        </ul>

        <p>Your personal information will be collected in Minnesota’s HMIS and, with your consent, shared with other service providers/homeless agencies. If you do not give permission for this agency to share your information, no other agency in the network will have access to it.</p>

        <h2>Why share your information?</h2>
        <ul>
          <li>Sharing reduces the amount of time you have to spend answering basic questions about your situation.</li>
          <li>Sharing allows agencies to focus on meeting your unique needs more quickly.</li>
          <li>Sharing makes it easier for multiple agencies to coordinate housing and services for you and your family.</li>
        </ul>

        <h2>Reminder:</h2>
        <p>The following fields are visible in HMIS by default:</p>
        <ul>
          <li>Name</li>
          <li>Social Security Number</li>
          <li>Date of Birth</li>
          <li>Race/Ethnicity</li>
          <li>Gender</li>
          <li>Contact Information</li>
          <li>Family Information</li>
        </ul>

        <h2>What transactional information might be shared within HMIS?</h2>
        <ul>
          <li>Family/Household information</li>
          <li>Name, birthdate, Social Security Number</li>
          <li>Gender, race, ethnicity</li>
          <li>Reasons for seeking services</li>
          <li>Living situation and housing history</li>
          <li>Services you receive</li>
          <li>If you are homeless or not</li>
          <li>Your income and income sources</li>
          <li>Public benefits you receive</li>
          <li>History of domestic violence</li>
          <li>Educational background</li>
          <li>Employment information/status</li>
          <li>Military history</li>
          <li>Health information, including physical health, HIV, behavioral health</li>
        </ul>

        <h2>Please check a box:</h2>
        <validated-section :errors="errors" field="agreement_selection">
          <div class="uk-width-1-1">
            <label class="checkbox-container">
              <input
                  type="radio"
                  v-model="agreement_selection"
                  :value="1"
                  id="agreement-agree"
                  name="agreement"
                  :disabled="hasMadeAgreement"
              />
              <span v-html="__(`tenant.tpa_content_section_8_agree`)"></span>
            </label>
          </div>
          <div class="uk-width-1-1">
            <label class="checkbox-container">
              <input
                  type="radio"
                  v-model="agreement_selection"
                  :value="0"
                  id="agreement-disagree"
                  name="agreement"
                  :disabled="hasMadeAgreement"
              />
              <span
                  v-html="__(`tenant.tpa_content_section_8_disagree`)"
              ></span>
            </label>
          </div>
        </validated-section>

        <p>When you sign this form, it shows that you understand the following:</p>
        <ul>
          <li>We will not deny you help if you do not want us to share your personal Transactional information. At the same time, sharing data does not guarantee that you will receive assistance.</li>
          <li>If you permit us to share your information, this consent is valid until canceled by you.</li>
          <li>If you permit us to share your information, you may change your mind and cancel this consent at any time. If you cancel this consent, your information will no longer be shared from that date forward.</li>
          <li>The HMIS Governing Board may amend this Release of Information and the Privacy Notice at any time without notice or consent. The Release of Information and Data Privacy Notice currently available can be found on the MN HMIS website at <a href="http://hmismn.org">hmismn.org</a>.</li>
          <li>Please treat information about my children age 17 or younger the same as mine.</li>
          <li>Verbal Consent obtained by phone</li>
        </ul>

      </fieldset>
      <div class="uk-margin-large-bottom">
        <fieldset>
          <div class="uk-container uk-margin-top">
            <div class="uk-grid" v-if="application.household">
              <div
                  class="uk-width-small-1-1 uk-width-medium-1-1 uk-width-large-1-2"
                  v-for="member in application.household.members"
              >
                <div v-if="signatureEnabled(member)">
                  <validated-section
                      :errors="errors"
                      :field="'signature_' + member.id"
                  >
                    <signature-box
                        @updated="onSignatureUpdated"
                        :user-key="member.id"
                        :is-signed="
                        signatureData[member.id] &&
                        signatureData[member.id]
                          .third_party_authorization_signed_at !== null
                      "
                        :completed="
                        signatureData[member.id] &&
                        signatureData[member.id].completed
                      "
                        :name="member.first_name + ' ' + member.last_name"
                        :color="programSettings['primary-program-color']"
                    ></signature-box>
                  </validated-section>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import SignatureBox from "../../../../../components/AppWizard/SignatureBox";
import ValidatedSection from "../../../../../components/Common/ValidatedSection";

export default {
  name: "ThirdPartyAuthorization",
  components: { ValidatedSection, SignatureBox },
  data() {
    return {
      agreement_selection: null,
      hasMadeAgreement: false,
      selection_pass_error: "",
      signatureData: {},
      errors: {},
    };
  },
  computed: {
    ...mapState("Application", {
      application: (state) => state.application,
    }),
    ...mapState("App", {
      env: (state) => state.env,
    }),
    ...mapGetters("Application", ["programSettings"]),
  },
  watch: {
    application(newValue) {
      if (newValue.household) {
        this.initData();
      }
    },
  },
  mounted() {
    if (this.application) this.initData();
  },
  methods: {
    initData() {
      this.agreement_selection = this.application.information_share_consent;
      if (this.agreement_selection !== null) {
        this.hasMadeAgreement = true;
      }

      _.each(this.application.household.members, (member) => {
        if (this.signatureEnabled(member)) {
          const signatureFiltered = member.signatures.find(
            (item) => item.key === `app-privacy-policy`
          );
          if (signatureFiltered) {
            this.$set(this.signatureData, member.id, {
              third_party_authorization_signed_at: signatureFiltered.created_at,
              completed: true,
            });
          }
        }
      });
    },

    signatureEnabled(member) {
      let res = false
      if (member.age >= 18 && (member.household_member_key === 1 || this.application.program.settings['require-memeber-signature']))
        res = true
      return res
    },

    onSignatureUpdated($event) {
      if (!this.signatureData[$event.key]) {
        this.$set(this.signatureData, $event.key, {});
      }
      this.$set(
        this.signatureData[$event.key],
        "third_party_authorization_signed_at",
        $event.isSigned ? moment().format("YYYY-MM-DD HH:mm") : null
      );
    },
    async validateStep() {
      this.errors = {};

      if (this.agreement_selection === null) {
        this.errors["agreement_selection"] = {
          errors: ["You must select one of the options above."],
        };
      }
      // Local Validation
      for (var i in this.application.household.members) {
        let member = this.application.household.members[i];
        if (this.signatureEnabled(member)) {
          if (!this.signatureData[member.id]) {
            this.errors["signature_" + member.id] = {
              errors: [
                member.first_name +
                  " " +
                  member.last_name +
                  " must sign this form as their name appears above.",
              ],
            };
          } else if (
            this.signatureData[member.id] &&
            this.signatureData[member.id].third_party_authorization_signed_at ==
              null
          ) {
            this.errors["signature_" + member.id] = {
              errors: [
                member.first_name +
                  " " +
                  member.last_name +
                  " must sign this form as their name appears above.",
              ],
            };
          }
        }
      }

      let errorCount = _.size(this.errors);

      if (errorCount > 0) return false;

      // Server Side Validation
      try {
        let response = await this.save();
        return true;
      } catch (err) {
        console.error(err);
      }
    },
    unitLedgerEntryVendors() {},
    async save() {
      let data = {
        signatures: this.signatureData,
        agreement: this.agreement_selection,
      };
      let r = axios.put(
        `/api/hope/applications/${this.application.id}/step-7`,
        data
      );

      window.setTimeout(async () => {
        await this.$store.dispatch("Application/fetch", this.application.id);
      }, 500);
    },
  },
};
</script>
